import {useCallback} from 'react';

import {Json} from '@/types/database.types';

import {supabase} from '../lib/utils/supabaseClass';
import {useCookieConsent} from '../providers/CookieConsentProvider';

type BaseEventPropertiesT = {
  /** The key-value pairs */
  [key: string]: string | number | boolean | null | undefined | unknown[];
};

type EcommerceItemT = {
  /** The item ID */
  item_id: string;
  /** The item name */
  item_name: string;
  /** The price */
  price: number;
  /** The quantity */
  quantity?: number;
};

type EventProperties = BaseEventPropertiesT & {
  /** The ecommerce items */
  items?: EcommerceItemT[];
};

type AnalyticsEvent = {
  event: string;
  userId: string;
  properties: EventProperties;
};

export type AnalyticsT = {
  trackPageView: (path: string) => void;
  trackEvent: (category: string, action: string, label?: string, value?: number) => void;
  trackEcommerce: (
    action: 'view_item' | 'add_to_cart' | 'purchase',
    properties: {
      items: EcommerceItemT[];
      currency?: string;
      value?: number;
    }
  ) => void;
  hasConsent: boolean;
  isInitialized: boolean;
};

/**
 * Hook to use the Analytics
 * @returns AnalyticsT
 */
export const useAnalytics = (): AnalyticsT => {
  const {hasAnalyticsConsent, isInitialized} = useCookieConsent();

  const pushToDataLayer = useCallback(
    async (event: string, properties?: EventProperties) => {
      const user = await supabase.auth.getUser();
      const userId = user.data?.user?.id;
      const noConsent = !hasAnalyticsConsent || !isInitialized;
      const noUserId = !userId;

      if (noConsent || noUserId) {
        console.error(noConsent ? 'No analytics consent' : 'No user id');
        return;
      }

      const analyticsEvent: AnalyticsEvent = {
        event,
        userId,
        properties: properties || {},
      };

      try {
        // Push to Google Analytics data layer
        window.dataLayer.push({
          event,
          cookie_flags: 'secure;samesite=none',
          cookie_domain: 'auto',
          ...properties,
          // Only set send_page_view for non-page_view events
          ...(event !== 'page_view' && {send_page_view: true}),
        });

        // Save to Supabase
        await supabase.from('Analytic_Events').insert({
          event: analyticsEvent.event,
          user_id: analyticsEvent.userId,
          properties: analyticsEvent.properties as Json,
        });

        // Save to Elasticsearch through Supabase Edge Function
        const {error} = await supabase.functions.invoke('analytics', {
          body: analyticsEvent,
        });

        if (error) {
          console.error('Failed to save analytics event:', error);
          throw error;
        }
      } catch (error) {
        console.error('Failed to save analytics event:', error);
      }
    },
    [hasAnalyticsConsent, isInitialized]
  );

  const trackPageView = useCallback(
    (path: string) => {
      pushToDataLayer('page_view', {
        page_path: path,
        page_title: document.title,
        page_location: window.location.href,
        non_interaction: true,
      });
    },
    [pushToDataLayer]
  );

  const trackEvent = useCallback(
    (category: string, action: string, label?: string, value?: number) => {
      pushToDataLayer('custom_event', {
        event_name: `${category}_${action}`,
        event_category: category,
        event_action: action,
        event_label: label,
        event_value: value,
        page_location: window.location.href,
        send_to: 'GA4',
        non_interaction: false,
      });
    },
    [pushToDataLayer]
  );

  const trackEcommerce = useCallback(
    (
      action: 'view_item' | 'add_to_cart' | 'purchase',
      properties: {
        items: EcommerceItemT[];
        currency?: string;
        value?: number;
      }
    ) => {
      pushToDataLayer(action, {
        currency: properties.currency || 'USD',
        value: properties.value,
        items: properties.items,
      });
    },
    [pushToDataLayer]
  );

  return {
    trackPageView,
    trackEvent,
    trackEcommerce,
    hasConsent: hasAnalyticsConsent,
    isInitialized,
  };
};
